import React from "react";
import moment from "moment";

const IDEAL_TRAINERS = 4;
const IDEAL_RECEPTIONISTS = 2;
const OVERSTAFF_THRESHOLD = 6;
const MIN_STAFF = 6;

const TITLES = {
  critical:    "Critical Gyms",
  overstaffed: "Overstaffed Gyms",
  best:        "Best Performing Gyms",
  absentee:    "Repeat Absentees",
  avg:         "Network Average",
};

/**
 * Center modal showing per-card detail breakdown.
 *
 * Props:
 *   modalKey: "critical" | "overstaffed" | "best" | "absentee" | "avg"
 *   gymDateMap: { [gymId]: { gym_name, dates: { [date]: { trainers, receptionists } } } }
 *   windowDates: string[]
 *   onClose: () => void
 */
const MatrixCardModal = ({ modalKey, gymDateMap, windowDates, onClose }) => {
  const todayStr = moment().format("YYYY-MM-DD");

  // Compute per-gym stats
  const gymRows = Object.entries(gymDateMap).map(([gymId, { gym_name, dates }]) => {
    const pastDates = windowDates.filter((d) => d <= todayStr);

    let criticalDays    = 0;
    let overstaffedDays = 0;
    let greenDays       = 0;
    let totalPresent    = 0;
    let peakCount       = 0;
    let peakDay         = "";
    let trainerDeficit  = 0;
    let fdDeficit       = 0;
    const absenteeMap   = {};

    pastDates.forEach((date) => {
      const day = dates[date];
      if (!day) return;
      const all      = [...(day.trainers || []), ...(day.receptionists || [])];
      const present  = all.filter((s) => s.isPresent).length;
      const tPresent = (day.trainers || []).filter((s) => s.isPresent).length;
      const rPresent = (day.receptionists || []).filter((s) => s.isPresent).length;

      totalPresent += present;
      if (present === 0)              criticalDays++;
      if (present > OVERSTAFF_THRESHOLD) overstaffedDays++;
      if (present >= MIN_STAFF)       greenDays++;
      if (present > peakCount)        { peakCount = present; peakDay = date; }
      if (tPresent < IDEAL_TRAINERS)     trainerDeficit++;
      if (rPresent < IDEAL_RECEPTIONISTS) fdDeficit++;

      all.forEach((s) => {
        const att = s.shift?.attendance_details;
        if (
          att?.status_value === "absent" ||
          (!att?.checkin_status && att?.status_value !== "on_leave" && att?.status_value !== "day_off")
        ) {
          absenteeMap[s.user_id] = (absenteeMap[s.user_id] || 0) + 1;
        }
      });
    });

    const avgStaff = pastDates.length > 0
      ? (totalPresent / pastDates.length).toFixed(1)
      : "—";
    const hasRepeatAbsentee = Object.values(absenteeMap).some((n) => n >= 3);

    return { gymId, gym_name, criticalDays, overstaffedDays, greenDays, avgStaff, peakCount, peakDay, trainerDeficit, fdDeficit, hasRepeatAbsentee };
  });

  const renderBody = () => {
    if (modalKey === "critical") {
      const rows = gymRows.filter((r) => r.criticalDays > 0).sort((a, b) => b.criticalDays - a.criticalDays);
      return (
        <table className="sdm-modal__table">
          <thead>
            <tr><th>Gym</th><th>Critical Days</th><th>Trainer Deficit</th><th>FD Deficit</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={4} style={{ textAlign: "center", color: "#64748b" }}>No critical gyms this week</td></tr>
            ) : rows.map((r) => (
              <tr key={r.gymId}>
                <td>{r.gym_name}</td>
                <td><span className="sdm-modal__badge sdm-modal__badge--red">{r.criticalDays}d</span></td>
                <td>{r.trainerDeficit > 0 ? <span className="sdm-modal__badge sdm-modal__badge--yellow">{r.trainerDeficit}d</span> : "—"}</td>
                <td>{r.fdDeficit > 0 ? <span className="sdm-modal__badge sdm-modal__badge--yellow">{r.fdDeficit}d</span> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (modalKey === "overstaffed") {
      const rows = gymRows.filter((r) => r.overstaffedDays > 0).sort((a, b) => b.overstaffedDays - a.overstaffedDays);
      return (
        <table className="sdm-modal__table">
          <thead>
            <tr><th>Gym</th><th>Overstaffed Days</th><th>Peak Staff</th><th>Peak Day</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={4} style={{ textAlign: "center", color: "#64748b" }}>No overstaffed gyms this week</td></tr>
            ) : rows.map((r) => (
              <tr key={r.gymId}>
                <td>{r.gym_name}</td>
                <td><span className="sdm-modal__badge sdm-modal__badge--purple">{r.overstaffedDays}d</span></td>
                <td>{r.peakCount}</td>
                <td>{r.peakDay ? moment(r.peakDay).format("ddd D MMM") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (modalKey === "best") {
      const rows = [...gymRows].sort((a, b) => b.greenDays - a.greenDays || parseFloat(b.avgStaff) - parseFloat(a.avgStaff));
      return (
        <table className="sdm-modal__table">
          <thead>
            <tr><th>#</th><th>Gym</th><th>Green Days</th><th>Avg Staff</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.gymId}>
                <td style={{ color: "#64748b", fontWeight: 600 }}>#{i + 1}</td>
                <td>{r.gym_name}</td>
                <td>
                  <span className={`sdm-modal__badge ${r.greenDays >= 5 ? "sdm-modal__badge--green" : r.greenDays >= 3 ? "sdm-modal__badge--yellow" : "sdm-modal__badge--red"}`}>
                    {r.greenDays}/7
                  </span>
                </td>
                <td>{r.avgStaff}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (modalKey === "absentee") {
      const rows = gymRows.filter((r) => r.hasRepeatAbsentee);
      return (
        <table className="sdm-modal__table">
          <thead>
            <tr><th>Gym</th><th>Trainer Deficit Days</th><th>FD Deficit Days</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={3} style={{ textAlign: "center", color: "#64748b" }}>No repeat absentee issues this week</td></tr>
            ) : rows.map((r) => (
              <tr key={r.gymId}>
                <td>{r.gym_name}</td>
                <td>{r.trainerDeficit > 0 ? <span className="sdm-modal__badge sdm-modal__badge--red">{r.trainerDeficit}d</span> : "—"}</td>
                <td>{r.fdDeficit > 0 ? <span className="sdm-modal__badge sdm-modal__badge--red">{r.fdDeficit}d</span> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (modalKey === "avg") {
      const rows = [...gymRows].sort((a, b) => parseFloat(b.avgStaff) - parseFloat(a.avgStaff));
      return (
        <table className="sdm-modal__table">
          <thead>
            <tr><th>Gym</th><th>Avg Staff/Day</th><th>Trainer Deficit</th><th>FD Deficit</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.gymId}>
                <td>{r.gym_name}</td>
                <td>
                  <span className={`sdm-modal__badge ${parseFloat(r.avgStaff) >= MIN_STAFF ? "sdm-modal__badge--green" : "sdm-modal__badge--yellow"}`}>
                    {r.avgStaff}
                  </span>
                </td>
                <td>{r.trainerDeficit > 0 ? <span className="sdm-modal__badge sdm-modal__badge--yellow">{r.trainerDeficit}d</span> : "—"}</td>
                <td>{r.fdDeficit > 0 ? <span className="sdm-modal__badge sdm-modal__badge--yellow">{r.fdDeficit}d</span> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return null;
  };

  return (
    <>
      <div className="sdm-modal-overlay" onClick={onClose} aria-hidden="true" />
      <div
        className="sdm-modal sdm-card-modal"
        role="dialog"
        aria-modal="true"
        aria-label={TITLES[modalKey] || "Details"}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sdm-modal__header">
          <div className="sdm-modal__title">{TITLES[modalKey]}</div>
          <button
            type="button"
            className="sdm-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="sdm-modal__body">
          {renderBody()}
        </div>
      </div>
    </>
  );
};

export default MatrixCardModal;
